import { useState, useEffect } from 'react';

export interface TimeInputProps {
  value?: { hours: number; minutes: number };
  onChange: (time: { hours: number; minutes: number } | undefined) => void;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export const TimeInput = ({
  value,
  onChange,
  label,
  error,
  disabled = false,
  className = '',
}: TimeInputProps) => {
  const [hours, setHours] = useState<string>(value?.hours.toString() || '');
  const [minutes, setMinutes] = useState<string>(value?.minutes.toString().padStart(2, '0') || '');
  const [isEnabled, setIsEnabled] = useState<boolean>(!!value);

  // Update local state when value prop changes
  useEffect(() => {
    if (value) {
      setHours(value.hours.toString());
      setMinutes(value.minutes.toString().padStart(2, '0'));
      setIsEnabled(true);
    } else {
      setHours('');
      setMinutes('');
      setIsEnabled(false);
    }
  }, [value]);

  const handleToggle = () => {
    const newEnabled = !isEnabled;
    setIsEnabled(newEnabled);
    
    if (newEnabled) {
      // Set default time to 12:00 PM
      const defaultHours = 12;
      const defaultMinutes = 0;
      setHours(defaultHours.toString());
      setMinutes(defaultMinutes.toString().padStart(2, '0'));
      onChange({ hours: defaultHours, minutes: defaultMinutes });
    } else {
      // Clear time
      setHours('');
      setMinutes('');
      onChange(undefined);
    }
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Allow empty or valid numbers
    if (newValue === '' || /^\d{0,2}$/.test(newValue)) {
      setHours(newValue);
      
      // Validate and update parent
      const hoursNum = parseInt(newValue, 10);
      const minutesNum = parseInt(minutes, 10) || 0;
      
      if (!isNaN(hoursNum) && hoursNum >= 0 && hoursNum <= 23) {
        onChange({ hours: hoursNum, minutes: minutesNum });
      }
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Allow empty or valid numbers
    if (newValue === '' || /^\d{0,2}$/.test(newValue)) {
      setMinutes(newValue);
      
      // Validate and update parent
      const hoursNum = parseInt(hours, 10) || 0;
      const minutesNum = parseInt(newValue, 10);
      
      if (!isNaN(minutesNum) && minutesNum >= 0 && minutesNum <= 59) {
        onChange({ hours: hoursNum, minutes: minutesNum });
      }
    }
  };

  const handleHoursBlur = () => {
    // Pad hours to 2 digits on blur
    if (hours && !isNaN(parseInt(hours, 10))) {
      const hoursNum = parseInt(hours, 10);
      if (hoursNum >= 0 && hoursNum <= 23) {
        setHours(hoursNum.toString());
      } else {
        // Reset to valid value
        setHours('12');
        onChange({ hours: 12, minutes: parseInt(minutes, 10) || 0 });
      }
    }
  };

  const handleMinutesBlur = () => {
    // Pad minutes to 2 digits on blur
    if (minutes && !isNaN(parseInt(minutes, 10))) {
      const minutesNum = parseInt(minutes, 10);
      if (minutesNum >= 0 && minutesNum <= 59) {
        setMinutes(minutesNum.toString().padStart(2, '0'));
      } else {
        // Reset to valid value
        setMinutes('00');
        onChange({ hours: parseInt(hours, 10) || 12, minutes: 0 });
      }
    }
  };

  const formatTime12Hour = () => {
    if (!isEnabled || !hours || !minutes) return '';
    
    const hoursNum = parseInt(hours, 10);
    const minutesNum = parseInt(minutes, 10);
    
    if (isNaN(hoursNum) || isNaN(minutesNum)) return '';
    
    const period = hoursNum >= 12 ? 'PM' : 'AM';
    const displayHours = hoursNum === 0 ? 12 : hoursNum > 12 ? hoursNum - 12 : hoursNum;
    const displayMinutes = minutesNum.toString().padStart(2, '0');
    
    return `${displayHours}:${displayMinutes} ${period}`;
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      <div className="space-y-3">
        {/* Toggle for all-day vs specific time */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="time-enabled"
            checked={isEnabled}
            onChange={handleToggle}
            disabled={disabled}
            className="h-4 w-4 text-primary-700 focus:ring-info-500 border-gray-300 rounded"
          />
          <label htmlFor="time-enabled" className="ml-2 text-sm text-gray-700">
            Set specific time (leave unchecked for all-day event)
          </label>
        </div>

        {/* Time inputs */}
        {isEnabled && (
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              {/* Hours input */}
              <div className="flex-1">
                <label htmlFor="hours" className="block text-xs text-gray-600 mb-1">
                  Hours (0-23)
                </label>
                <input
                  id="hours"
                  type="text"
                  inputMode="numeric"
                  value={hours}
                  onChange={handleHoursChange}
                  onBlur={handleHoursBlur}
                  disabled={disabled}
                  placeholder="12"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
                    error ? 'border-red-500' : 'border-gray-300'
                  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  maxLength={2}
                />
              </div>

              <span className="text-2xl text-gray-400 pt-5">:</span>

              {/* Minutes input */}
              <div className="flex-1">
                <label htmlFor="minutes" className="block text-xs text-gray-600 mb-1">
                  Minutes (0-59)
                </label>
                <input
                  id="minutes"
                  type="text"
                  inputMode="numeric"
                  value={minutes}
                  onChange={handleMinutesChange}
                  onBlur={handleMinutesBlur}
                  disabled={disabled}
                  placeholder="00"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-info-500 ${
                    error ? 'border-red-500' : 'border-gray-300'
                  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  maxLength={2}
                />
              </div>
            </div>

            {/* 12-hour format display */}
            {formatTime12Hour() && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">12-hour format:</span> {formatTime12Hour()}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};
